import { z } from "zod";

export const fileCapabilitiesSchema = z.object({
  canRename: z.boolean(),
  canMove: z.boolean(),
  canDelete: z.boolean(),
  canDownload: z.boolean(),
  canUploadVersion: z.boolean(),
  canRestoreVersion: z.boolean(),
});

export const fileVersionSchema = z.object({
  id: z.string(),
  versionNumber: z.number().int().positive(),
  createdAt: z.string(),
  uploadedByUserId: z.string().nullable(),
  uploadedByDisplayName: z.string(),
  sizeBytes: z.number().nonnegative(),
  contentType: z.string(),
  restoredFromVersionId: z.string().nullable(),
  isCurrent: z.boolean(),
});

export const canonicalFileSchema = z.object({
  id: z.string(),
  name: z.string(),
  isFolder: z.boolean(),
  description: z.string().nullable(),
  scope: z.enum(["personal", "firm", "matter", "mass_tort", "template"]),
  variables: z.unknown().optional(),
  templateCategories: z.unknown().optional(),
  templateModuleShortcuts: z.unknown().optional(),
  parentId: z.string().nullable(),
  ownerUserId: z.string().nullable(),
  matterId: z.string().nullable(),
  massTortId: z.string().nullable(),
  revision: z.number().int().positive(),
  dateCreated: z.string(),
  dateModified: z.string(),
  hasChildren: z.boolean(),
  systemFolder: z.string().nullable(),
  skillId: z.string().nullable(),
  contentType: z.string().nullable(),
  sizeBytes: z.number().nullable(),
  publishedProcessingId: z.string().nullable(),
  currentVersionId: z.string().nullable(),
  currentVersionNumber: z.number().nullable(),
  hasPreviousVersions: z.boolean(),
  capabilities: fileCapabilitiesSchema,
});

export const canonicalFileWriteSchema = canonicalFileSchema.extend({
  publishedVersion: fileVersionSchema.nullable(),
});
export type CanonicalFile = z.infer<typeof canonicalFileSchema>;
export const fileSearchHitSchema = z.object({
  file: canonicalFileSchema,
  classificationTags: z.array(z.string()),
  matchedFields: z.array(
    z.enum(["name", "description", "tags", "model", "page", "keywords"]),
  ),
  excerpt: z.string(),
  pageNumber: z.number().int().positive().nullable(),
});
export const fileSearchResultsSchema = z.object({
  data: z.array(fileSearchHitSchema),
  nextCursor: z.string().nullable(),
  matchMode: z.enum(["exact", "filename_prefix", "filename_typo"]),
});
export type FileSearchHit = z.infer<typeof fileSearchHitSchema>;
export type FileSearchScope =
  | { scope: "all" }
  | { scope: "matter"; matterId: string }
  | { scope: "mass_tort"; massTortId: string };
export type CanonicalFileWrite = z.infer<typeof canonicalFileWriteSchema>;
export type FileVersion = z.infer<typeof fileVersionSchema>;
export type FileDestination =
  | { parentId: string }
  | { scope: "personal" | "firm" }
  | { scope: "matter"; matterId: string }
  | { scope: "mass_tort"; massTortId: string };
